import { FormEvent, useEffect, useState } from 'react';
import cx from 'classnames';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';
import {
  EventType,
  getStandAlonePageName,
  logAnalyticsEvent,
  PageTags,
} from '@pass-frontend/common/analytics';
import { ContentWidthWrapper } from '@pass-frontend/common/components/content-width-wrapper/ui';
import { STANDALONE_PATHS } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { MemberCheckStatus } from '@pass-frontend/data-access/common/edge';
import { useEligibilityCheck } from '@pass-frontend/features/standalone/data-bridge';
import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';
import { NotEligibleModal } from '../not-eligible-modal/not-eligible-modal';
import { YouAreEligibleModal } from '../you-are-eligible-modal/you-are-eligible-modal';

/* eslint-disable-next-line */
export interface EligibilityCheckFormProps {}

const StyledEligibilityCheckForm = styled.div`
  h1 {
    margin-top: 32px;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 24px;
  }

  .name {
    flex-direction: column;
  }

  input:focus-visible {
    outline: none;
    border-color: #222222;
  }

  .btn {
    width: 100%;
  }

  .loading {
    background-color: #b1b2b4;
  }

  .disabled {
    pointer-events: none;
  }

  ${MEDIA_QUERY.desktop} {
    width: 616px;
    margin: auto;

    h1 {
      margin-top: 56px;
      margin-bottom: 8px;
    }

    p {
      margin-bottom: 32px;
    }

    .name {
      flex-direction: row;

      input {
        width: 296px;
      }
    }

    .btn {
      width: 160px;
      font-size: 16px;
      line-height: 20px;
      padding: 10px 0;
    }
  }
`;

const StyledInput = styled.div.attrs({
  className: 'is-display-flex is-flex-direction-column',
})`
  margin-bottom: 16px;

  label {
    margin-bottom: 4px;
  }

  input {
    border: 1px solid #b3b3b3;
    border-radius: 32px;
    height: 48px;
    padding: 14px 16px;
  }

  ${MEDIA_QUERY.desktop} {
    margin-bottom: 24px;

    label {
      margin-bottom: 8px;
    }

    &:first-child {
      margin-right: 24px;
    }
  }
`;

// NOTE: is this still being used? can we delete this?
export function EligibilityCheckForm(props: EligibilityCheckFormProps) {
  const { t } = useIntl();

  const {
    fetchMemberByEligibility,
    memberCheckInProgress,
    registrationUrl,
    serviceSector,
    memberCode,
    updateMemberBigN,
    isNotEligibleModalOpen,
    onConfirmModalClose,
    isEligibleModalOpen,
    eligibilityCheckStatus,
  } = useEligibilityCheck();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  const router = useRouter();

  const onEligibilitySubmit = (event: FormEvent) => {
    event.preventDefault();

    if (firstName && lastName && dob) {
      updateMemberBigN({
        firstName: firstName,
        lastName: lastName,
        birthDate: dob,
      });

      fetchMemberByEligibility(
        {
          firstName: firstName,
          lastName: lastName,
          birthDate: dob,
        },
        '',
        false,
      );

      logAnalyticsEvent(
        EventType.FormSubmit,
        {
          type: EventType.FormSubmit,
          [PageTags.TriggerUIElementName]: 'Submit',
        },
        event.nativeEvent,
      );
    }
  };

  useEffect(() => {
    if (eligibilityCheckStatus === MemberCheckStatus.internalServerError) {
      router?.push(STANDALONE_PATHS.ZIP_CODE_CHECK);
    }

    memberCheckInProgress
      ? setIsSubmitDisabled(true)
      : setIsSubmitDisabled(false);
  }, [memberCheckInProgress, eligibilityCheckStatus]);

  useEffect(() => {
    firstName && lastName && dob
      ? setIsSubmitDisabled(false)
      : setIsSubmitDisabled(true);
  }, [firstName, lastName, dob]);

  const logAdobeEventOnModalCancel = () => {
    // eslint-disable-next-line no-restricted-globals
    event?.stopPropagation();
    logAnalyticsEvent(
      EventType.PageNav,
      {
        pageName: getStandAlonePageName(STANDALONE_PATHS.ELIGIBILITY_CHECK),
      },
      // eslint-disable-next-line no-restricted-globals
      event,
    );
  };

  const handleBirthdateInput = (e) => {
    let input = e.target.value;
    if (input.length < dob.length) {
      if (dob[dob.length - 1] === '/') {
        input = input.slice(0, input.length - 1);
      }
    }
    //it replaces all non-numeric characters
    input = input.replace(/[^0-9]/g, '');
    // Format input with slashes
    if (input.length >= 2 && input.length < 4) {
      input = `${input.slice(0, 2)}/${input.slice(2)}`;
    } else if (input.length >= 4) {
      input = `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4, 8)}`;
    }
    setDob(input);
  };

  return (
    <ContentWidthWrapper>
      <StyledEligibilityCheckForm>
        <h1 className="is-h1 has-text-blue-1">
          {t('eligibilityCheck.form.title')}
        </h1>
        <p className="is-body-1-regular">
          {t('eligibilityCheck.form.subTitle')}
        </p>
        <form onSubmit={(e: FormEvent) => onEligibilitySubmit(e)}>
          <div className="is-display-flex name">
            <StyledInput>
              <label className="is-body-2-regular" htmlFor="first">
                {t('eligibilityCheck.form.firstName')}
              </label>
              <input
                type="text"
                id="first"
                name="first"
                autoComplete="do-not-autofill"
                onChange={(e) => setFirstName(e.target.value)}
              />
            </StyledInput>

            <StyledInput>
              <label className="is-body-2-regular" htmlFor="last">
                {t('eligibilityCheck.form.lastName')}
              </label>
              <input
                type="text"
                id="last"
                name="last"
                onChange={(e) => setLastName(e.target.value)}
              />
            </StyledInput>
          </div>

          <StyledInput>
            <label className="is-body-2-regular" htmlFor="date-of-birth">
              {t('eligibilityCheck.form.dateOfBirth')}
            </label>
            <input
              type="text"
              value={dob}
              id="date-of-birth"
              name="dateOfBirth"
              placeholder="MM/DD/YYYY"
              onChange={handleBirthdateInput}
            />
          </StyledInput>

          <input
            type="submit"
            value={
              memberCheckInProgress
                ? t('eligibilityCheck.submittingButtonText')
                : t('eligibilityCheck.submitButtonText')
            }
            disabled={isSubmitDisabled || memberCheckInProgress}
            className={cx('btn btn-primary', {
              disabled: isSubmitDisabled || memberCheckInProgress,
              loading: memberCheckInProgress,
            })}
            data-ui-element-name="eligibility-check-form-submit-btn"
          />

          <YouAreEligibleModal
            isOpen={isEligibleModalOpen}
            onRequestedClose={() => {
              onConfirmModalClose();
              logAdobeEventOnModalCancel();
            }}
            registrationUrl={registrationUrl}
            data-ui-element-name="you-are-eligible-modal"
            aria-modal={true}
            serviceSector={serviceSector}
            memberCode={memberCode}
          />

          <NotEligibleModal
            isOpen={isNotEligibleModalOpen}
            onRequestedClose={() => {
              onConfirmModalClose();
              logAdobeEventOnModalCancel();
            }}
            data-ui-element-name="we-cant-find-you-modal"
            aria-modal={true}
          />
        </form>
      </StyledEligibilityCheckForm>
    </ContentWidthWrapper>
  );
}

export default EligibilityCheckForm;
